import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {TableItemForGridFormat} from './data';
import DocumentToolsCodes from "./in-grid-codes-view";
import {AssociationRead, AssociationReadWReferenceLoad, TagRead, TagType, isTranscriptExchangeTag} from "../../schemas";
import {GridView, useNavigationContext} from "../../contexts/NavContext/nav-context";
import {SlArrowRight} from "react-icons/sl";
import {MdOpenInFull} from "react-icons/md";
import {useEnhancedDocumentsContext} from "../../contexts/doc_contexts";
import {useTagContext} from "../../contexts/tag_context";
import {getAssociations, getAssociationsWithReferences} from "../../api";
import {withApiErrorHandler} from "../../error_handling";
import {Row} from "@tanstack/react-table";
import {calculateScoreColor} from "../../colors";
import {getRelevanceLevelFromScore} from "../../routines/schemas";
import {TAG_UPDATE_COMPLETED} from "../../events";

// Cache for association references: keyed by `${docId}:${tagId}`
const associationReferencesCache = new Map<string, AssociationReadWReferenceLoad>();

// Track pending fetches to avoid duplicate requests
const pendingFetches = new Map<string, Promise<void>>();

// Clear cache when tag updates complete (references may have changed)
if (typeof window !== 'undefined') {
    window.addEventListener(TAG_UPDATE_COMPLETED, () => {
        associationReferencesCache.clear();
        pendingFetches.clear();
    });
}


interface CodeRenderProps {
    original: TableItemForGridFormat;
    tagType: TagType;
    /** Filter mode for TAG type: 'codes' excludes transcript tags, 'transcript' shows only transcript tags */
    tagFilter?: 'codes' | 'transcript';
}

const CodeRender: React.FC<CodeRenderProps> = ({original, tagType, tagFilter}) => {
    const {tagMap, visibleTagIds} = useTagContext()!;
    const {navigateTo} = useNavigationContext()!;

    const docId = original.id;

    // Keep associations in state and refresh them when loadedDocMap changes
    const [associations, setAssociations] = React.useState<AssociationRead[]>([]);
    const [filteredTagMap, setFilteredTagMap] = React.useState<Map<string, TagRead>>(new Map());
    const [filteredAssociationMap, setFilteredAssociationMap] = React.useState<Map<string, AssociationRead>>(new Map());

    const {loadedDocMap} = useEnhancedDocumentsContext()!;
    // Whenever the doc in loadedDocMap changes, update associations state
    useEffect(() => {
        const doc = loadedDocMap.get(docId);
        setAssociations(doc?.applied_tag_associations ?? []);
    }, [docId, loadedDocMap]);

    // Whenever associations, tagMap, or visibleTagIds change, recompute filtered maps
    useEffect(() => {
        const newTagMap = new Map<string, TagRead>();
        const newAssocMap = new Map<string, AssociationRead>();

        for (const association of associations) {
            if (association.is_positive) {
                const tag = tagMap.get(association.tag_id);
                if (tag && tag.type === tagType) {
                    // Apply transcript filter when tagType is TAG
                    if (tagFilter === 'codes' && isTranscriptExchangeTag(tag)) continue;
                    if (tagFilter === 'transcript' && !isTranscriptExchangeTag(tag)) continue;
                    // Only include if visibleTagIds is null (show all) or contains this tag
                    if (visibleTagIds === null || visibleTagIds.has(tag.id)) {
                        newTagMap.set(tag.id, tag);
                        newAssocMap.set(association.tag_id, association);
                    }
                }
            }
        }

        setFilteredTagMap(newTagMap);
        setFilteredAssociationMap(newAssocMap);
    }, [associations, tagMap, visibleTagIds, tagType, tagFilter]);

    // Update function will refresh the associations in loadedDocMap
    const updateDocMap = async () => {
        const updated = await withApiErrorHandler(
            () => getAssociations(docId),
            {titleQualifier: "Error Fetching Associations", navigateTo}
        );

        // Write through to loadedDocMap if your context expects it
        if (updated) {
            setAssociations(updated);
        }
    };

    return (
        <DocumentToolsCodes
            updateParent={updateDocMap}
            filteredAssociationMap={filteredAssociationMap}
            filteredTagMap={filteredTagMap}
            tagType={tagType}
        />
    );
};

interface IdRenderProps {
    row: Row<TableItemForGridFormat>;
    gridView: GridView | undefined;
}

const IdRender: React.FC<IdRenderProps> = ({row, gridView}) => {
    const {setDocument, getDocument} = useEnhancedDocumentsContext()!;

    const {setIsDocExpanded} = useNavigationContext()!;
    const docId = row.original.id; // ✅ your document ID

    return (
        <div className="flex items-center justify-start gap-1.5 px-1 h-full">
            {/* Selection checkbox */}
            <input
                type="checkbox"
                className="cursor-pointer accent-indigo-500"
                checked={row.getIsSelected()}
                onChange={row.getToggleSelectedHandler()}
                onClick={(e) => e.stopPropagation()}
            />

            {/* Arrow button (hidden in MyDashboard) */}
            {gridView !== GridView.MyDashboard && (
                <button
                    className="p-1 rounded-md border border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-colors"
                    onClick={async (e) => {
                        e.stopPropagation();
                        const doc = await getDocument(docId);
                        if (doc) setDocument(doc);
                    }}
                    title="Select Document"
                >
                    <SlArrowRight className="w-3 h-3"/>
                </button>
            )}

            {/* Open full view — expands in-place instead of navigating away */}
            <button
                className="p-1 rounded-md border border-slate-200 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 hover:border-indigo-200 transition-colors"
                onClick={async (e) => {
                    e.stopPropagation();
                    const doc = await getDocument(docId);
                    if (!doc) return;
                    setDocument(doc);
                    setIsDocExpanded(true);
                }}
                title="Open Full View"
            >
                <MdOpenInFull className="w-3 h-3"/>
            </button>
        </div>
    );
};


/**
 * Props for the ExplanationRender component
 */
interface ExplanationRenderProps {
    original: TableItemForGridFormat;
    tagId: string;
    tagName: string;
}

interface ReferenceWithExplanation {
    quote: string;
    explanation?: string;
}

/**
 * Renders the explanation for a specific tag for a document.
 * Shows the relevance score, quoted text, and explanation from the references.
 * Lazy-loads references from the API and caches them.
 */
const ExplanationRender: React.FC<ExplanationRenderProps> = ({original, tagId, tagName}) => {
    const {loadedDocMap} = useEnhancedDocumentsContext()!;
    const {navigateTo} = useNavigationContext()!;

    const docId = original.id;
    const cacheKey = `${docId}:${tagId}`;

    const [isLoading, setIsLoading] = useState(false);
    const [cachedAssoc, setCachedAssoc] = useState<AssociationReadWReferenceLoad | null>(
        () => associationReferencesCache.get(cacheKey) ?? null
    );

    // Get basic association from loadedDocMap (for score display while loading)
    const basicAssociation = useMemo(() => {
        const doc = loadedDocMap.get(docId);
        if (!doc?.applied_tag_associations) return null;
        // Prefer positive, fall back to any scored association for this tag
        return doc.applied_tag_associations.find(
            (a) => a.tag_id === tagId && a.is_positive
        ) ?? doc.applied_tag_associations.find(
            (a) => a.tag_id === tagId && a.score != null
        ) ?? null;
    }, [loadedDocMap, docId, tagId]);

    // Fetch references if not cached
    const fetchReferences = useCallback(async () => {
        // Check cache first
        if (associationReferencesCache.has(cacheKey)) {
            setCachedAssoc(associationReferencesCache.get(cacheKey)!);
            return;
        }

        // Check if already fetching
        if (pendingFetches.has(cacheKey)) {
            await pendingFetches.get(cacheKey);
            setCachedAssoc(associationReferencesCache.get(cacheKey) ?? null);
            return;
        }

        setIsLoading(true);

        const fetchPromise = (async () => {
            const associations = await withApiErrorHandler(
                () => getAssociationsWithReferences([docId], tagId),
                {titleQualifier: "Error Fetching References", navigateTo}
            );

            if (associations && associations.length > 0) {
                // Prefer positive association (relevant), fall back to any association
                // with references — negative associations also have "why not relevant"
                // explanations that should display in the column.
                const assoc = associations.find(a => a.doc_id === docId && a.is_positive)
                    ?? associations.find(a => a.doc_id === docId && a.references?.length);
                if (assoc) {
                    associationReferencesCache.set(cacheKey, assoc);
                    setCachedAssoc(assoc);
                }
            }
        })();

        pendingFetches.set(cacheKey, fetchPromise);

        try {
            await fetchPromise;
        } finally {
            pendingFetches.delete(cacheKey);
            setIsLoading(false);
        }
    }, [cacheKey, docId, tagId, navigateTo]);

    // Fetch on mount if not cached
    useEffect(() => {
        if (!cachedAssoc && basicAssociation) {
            fetchReferences();
        }
    }, [cachedAssoc, basicAssociation, fetchReferences]);

    // Extract references from cached association
    const references: ReferenceWithExplanation[] = useMemo(() => {
        if (!cachedAssoc?.references) return [];
        return cachedAssoc.references
            .filter((ref) => ref.referenced_text)
            .map((ref) => ({
                quote: ref.referenced_text,
                explanation: ref.explanation,
            }));
    }, [cachedAssoc]);

    // Use cached association for score, fall back to basic
    const association = cachedAssoc ?? basicAssociation;

    if (!association) {
        return (
            <div className="text-xs text-zinc-400 italic">
                No data
            </div>
        );
    }

    const score = association.score ?? 0;
    const level = getRelevanceLevelFromScore(score);
    const color = calculateScoreColor(score);

    return (
        <div className="text-xs space-y-1">
            {/* Score badge */}
            <div className="flex items-center gap-2">
                <span
                    className="px-2 py-0.5 rounded-full text-white text-[10px] font-medium"
                    style={{backgroundColor: color}}
                >
                    {level}
                </span>
                <span className="text-zinc-500 truncate" title={tagName}>
                    {tagName}
                </span>
            </div>

            {/* References with quotes and explanations */}
            {isLoading ? (
                <div className="text-zinc-400 italic flex items-center gap-1">
                    <span className="animate-spin h-3 w-3 border border-zinc-400 border-t-transparent rounded-full"/>
                    Loading...
                </div>
            ) : references.length > 0 ? (
                <div className="space-y-2">
                    {references.map((ref, i) => (
                        <div key={i} className="text-zinc-700">
                            <div className="whitespace-pre-wrap line-clamp-3 italic">
                                &ldquo;{ref.quote}&rdquo;
                            </div>
                            {ref.explanation && (
                                <div className="text-zinc-600 mt-0.5">
                                    <span className="font-medium">Why relevant:</span> {ref.explanation}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-zinc-400 italic">
                    No explanation available
                </div>
            )}
        </div>
    );
};

export {CodeRender, IdRender, ExplanationRender};
